/**
 * The four project audiences, as the UI sees them.
 *
 * The API derives `audience` from stored state and never stores it — see
 * project_access.audience_of. The frontend therefore renders what it is told
 * and never re-derives it from `visibility`, which is only one of the three
 * fields involved.
 *
 * "Product" is deliberately not an audience. It is the combination
 * audience !== 'private' plus a Joveo grant of 'editor' — a fact about the
 * other axis — so it appears in the create presets and in the Joveo chip,
 * not here.
 */

export type Audience = "private" | "partner" | "public" | "shared";
export type JoveoRole = "none" | "viewer" | "editor" | "admin";

export interface ProjectAudience {
  audience?: Audience;
  partner?: { id: string; slug: string; name: string } | null;
  joveoRole?: JoveoRole | null;
}

const AUDIENCE_LABEL: Record<Audience, string> = {
  private: "Private",
  shared: "Shared",
  partner: "Partner",
  public: "Public",
};

const AUDIENCE_HINT: Record<Audience, string> = {
  private: "Only people at Joveo can open this project.",
  shared: "Shared with the external people and partners you name.",
  partner: "Shared with one partner, and nobody else outside Joveo.",
  public: "Anyone with Cortex access can read this project.",
};

const AUDIENCE_TONE: Record<Audience, string> = {
  private: "bg-slate-100 text-slate-600 ring-slate-200",
  shared: "bg-sky-50 text-sky-700 ring-sky-200",
  partner: "bg-violet-50 text-violet-700 ring-violet-200",
  public: "bg-amber-50 text-amber-700 ring-amber-200",
};

const JOVEO_LABEL: Record<JoveoRole, string> = {
  none: "No access",
  viewer: "Viewer",
  editor: "Editor",
  admin: "Admin",
};

/** `audience` missing means a response older than the field, or an object
 *  trimmed on the way to a component. Fall back to the RESTRICTIVE answer:
 *  every helper below treats "private" as "offer nothing". The old fallback
 *  read `visibility`, which cannot tell Partner from Public — both are
 *  'external' — so a trimmed partner project came back "shared" and was
 *  offered anonymous link sharing, the one thing Partner promises it will
 *  never have. */
export function audienceOf(p: ProjectAudience): Audience {
  if (p.audience) return p.audience;
  return p.partner ? "partner" : "private";
}

export function audienceLabel(p: ProjectAudience): string {
  const a = audienceOf(p);
  return a === "partner" && p.partner ? `Partner: ${p.partner.name}` : AUDIENCE_LABEL[a];
}

export function audienceHint(p: ProjectAudience): string {
  return AUDIENCE_HINT[audienceOf(p)];
}

/** True where naming individual external people makes sense at all.
 *  Deliberately grantee-agnostic — use the two below where you know who the
 *  grant is FOR, because a partner-locked project only takes grants for its
 *  own partner's people and the API 422s anything else. */
export function supportsNamedExternalAccess(p: ProjectAudience): boolean {
  return audienceOf(p) !== "private";
}

/** Offering a project when configuring access FOR A PARTNER. */
export function supportsPartnerAccess(p: ProjectAudience, partnerId: string): boolean {
  const a = audienceOf(p);
  if (a === "private") return false;
  return a !== "partner" || p.partner?.id === partnerId;
}

/** Offering a project when configuring access FOR ONE PERSON, given the
 *  partners they belong to. */
export function supportsUserAccess(
  p: ProjectAudience, userPartnerIds: Set<string>,
): boolean {
  const a = audienceOf(p);
  if (a === "private") return false;
  return a !== "partner" || userPartnerIds.has(p.partner?.id ?? "");
}

/** True where a project DEFAULT SCOPE is legal.
 *
 *  Every external audience: build_access_filter reads the default rules for
 *  every external principal on all three — a partner's mode='default' and
 *  'default_plus' branches and the partnerless fallback. This was `=== "public"`
 *  on the premise that a default rule elsewhere "would grant nobody", which
 *  confused scope with grant: a default rule admits nobody anywhere
 *  (_RULE_GRANT_SQL has no default leg), it only narrows readers who are
 *  already in.
 *
 *  Private is the exception and stays out: nothing outside Joveo ever reads
 *  one, so the scope would apply to nobody. Its equivalent is the Joveo role. */
export function supportsDefaultRules(p: ProjectAudience): boolean {
  return audienceOf(p) !== "private";
}

export function AudienceChip({ project, className = "" }: {
  project: ProjectAudience; className?: string;
}) {
  const a = audienceOf(project);
  return (
    <span
      title={AUDIENCE_HINT[a]}
      className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded ring-1 ${AUDIENCE_TONE[a]} ${className}`}
    >
      {audienceLabel(project)}
    </span>
  );
}

export function JoveoChip({ role, className = "" }: {
  role?: JoveoRole | null; className?: string;
}) {
  const r: JoveoRole = role ?? "none";
  return (
    <span
      title={`Everyone at Joveo: ${JOVEO_LABEL[r]}`}
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-white text-slate-600 ring-1 ring-slate-200 ${className}`}
    >
      <span className="text-slate-400">Joveo</span>
      {JOVEO_LABEL[r]}
    </span>
  );
}

/** The audience selector's options, in the order they are offered. Each one
 *  maps onto exactly the stored fields, so what the UI claims and what
 *  audience_of derives server-side cannot drift. */
export const AUDIENCE_CHOICES: Array<{
  key: Audience; label: string; hint: string; needsSuperAdmin: boolean;
}> = [
  { key: "private", label: "Only Joveo", needsSuperAdmin: false,
    hint: "Never leaves Joveo. No link, no partner, no outside reader." },
  { key: "shared", label: "Named people outside", needsSuperAdmin: false,
    hint: "The external people and partners you grant, one by one." },
  { key: "partner", label: "One partner", needsSuperAdmin: true,
    hint: "That partner's people only. Nobody else outside Joveo." },
  { key: "public", label: "Anyone with Cortex", needsSuperAdmin: true,
    hint: "Every signed-in external reader, with no grant of their own." },
];


/** True where an anonymous share link is legal. Private keeps nothing
 *  outside Joveo and Partner is an exclusivity promise, so create_share 409s
 *  both — offering "New link" there is a dead control. */
export function supportsLinkSharing(p: ProjectAudience): boolean {
  const a = audienceOf(p);
  return a === "shared" || a === "public";
}

/** Can THIS viewer move THIS project to THAT audience?
 *
 *  Mirrors the server gate in admin_projects.update_project exactly, which
 *  authorises a NON-super caller on the partners at BOTH ENDS of the change:
 *
 *      ends = {current.partner_id, pending.partner_id} - {None}
 *      allowed = ends and every end is administered by the caller
 *      and never when share_by_default moves
 *      — or it is Private <-> Shared and the caller is this project's admin
 *        (is_project_admin_flip)
 *
 *  The previous form took only the current audience, so it answered one
 *  question with another: it refused Shared -> Partner and Private ->
 *  Partner for a partner admin the server accepts, and it could not express
 *  "this end is fine, that one is not". Locking on the target alone left a
 *  non-super able to click an audience whose PATCH the server then ignored —
 *  200, green "Saved", nothing changed — and locking on "not super" alone
 *  shut out the partner admin the server accepts.
 *
 *  Caveat worth knowing: /api/admin/partners is super-admin-only, so a
 *  partner admin reaching "One partner" finds an empty picker and the
 *  Save stays disabled. That is a separate limit, signposted in the UI.
 */
export function canChangeAudienceTo(
  p: ProjectAudience, target: Audience,
  isSuperAdmin: boolean, partnerAdminOf: Set<string>,
  isProjectAdmin = false,
): boolean {
  if (isSuperAdmin) return true;
  const from = audienceOf(p);
  if (from === target) return true;              // nothing to authorise
  // Opening a project to every external reader, or closing it from that, is
  // never partner administration.
  if (from === "public" || target === "public") return false;
  // Private <-> Shared is this project's admin's to make
  // (admin_projects.is_project_admin_flip, 2026-10-12 self-serve sharing §5):
  // Shared admits nobody outside Joveo until someone is named.
  if (isProjectAdmin
      && ((from === "private" && target === "shared")
          || (from === "shared" && target === "private"))) return true;
  const fromEnd = from === "partner" ? (p.partner?.id ?? "") : null;
  if (fromEnd !== null && !partnerAdminOf.has(fromEnd)) return false;
  if (target === "partner") return partnerAdminOf.size > 0;
  // Private <-> Shared has a partner at neither end, so partner
  // administration doesn't reach it (a project admin was let through above);
  // with a partner at the FROM end it is that partner admin's to make.
  return fromEnd !== null;
}

/** True where this viewer may change the audience to ANYTHING at all — the
 *  one answer a caller needs when it is deciding whether to SEND the
 *  audience fields rather than whether to offer one button. */
export function canChangeAudience(
  p: ProjectAudience, isSuperAdmin: boolean, partnerAdminOf: Set<string>,
  isProjectAdmin = false,
): boolean {
  if (isSuperAdmin) return true;
  return (["private", "shared", "partner", "public"] as Audience[])
    .some(a => a !== audienceOf(p)
               && canChangeAudienceTo(p, a, isSuperAdmin, partnerAdminOf, isProjectAdmin));
}

/** One row of GET /api/admin/partners/{id}/doc-access-summary — the server's
 *  partner_project_reach, which mirrors the resolver for a member of the
 *  partner. The partner Access tab sorts on canRead and never re-derives it. */
export interface PartnerReach {
  mode: "default" | "default_plus" | "override" | null;
  ruleCount: number;
  canRead: boolean;
  via: "everyone" | "rules" | "default" | "enable" | null;
  reason: "not_given" | "not_published" | "nothing_ticked" | "docs_app_off" | null;
}

/** The partner Access tab's one line per project: why it can or can't read. */
export function describePartnerReach(r: PartnerReach | undefined): string {
  if (!r || r.reason === "not_given") return "Not given to this partner";
  if (r.reason === "not_published") return "Not published yet";
  if (r.reason === "docs_app_off") return "Docs app is switched off for this partner";
  if (r.reason === "nothing_ticked") return "Only what's ticked — and nothing is";
  const scope = r.mode === "override" ? `Only ${r.ruleCount} ticked`
    : r.mode === "default_plus" && r.ruleCount ? `External view + ${r.ruleCount} ticked`
    : "External view";
  const source = r.via === "everyone" ? "Public, open to everyone outside Joveo"
    : r.via === "enable" ? "Through Enable" : "";
  return source ? `${source} · ${scope}` : scope;
}

/** One project in GET /api/admin/users/{id}/doc-access-reach — the resolver's
 *  own answer for one outside person (or a pending invite, once accepted). */
export interface PersonReach {
  mode: "default" | "default_plus" | "override" | null;
  ruleCount: number;
  canRead: boolean;
  via: "everyone" | "shared" | "own" | "partner" | "enable" | null;
  partners: string[];
  reason: "not_given" | "not_published" | "nothing_ticked" | "docs_app_off" | "deactivated" | null;
}

/** The External Users access tab's one line per project. */
export function describePersonReach(r: PersonReach | undefined): string {
  if (!r || r.reason === "not_given") return "Not given to them";
  if (r.reason === "not_published") return "Not published yet";
  if (r.reason === "docs_app_off") return "Docs app is switched off for their partner";
  if (r.reason === "nothing_ticked") return "Only what's ticked — and nothing is";
  if (r.reason === "deactivated") return "Account deactivated";
  const source = r.via === "everyone" ? "Public, open to everyone outside Joveo"
    : r.via === "shared" ? "Shared with them by name"
    : r.via === "own" ? "Their own access"
    : r.via === "partner" ? `Through ${r.partners.join(", ")}`
    : "Through Enable";
  const scope = r.mode === "override" ? ` · only ${r.ruleCount} ticked`
    : r.mode === "default_plus" && r.ruleCount ? ` · + ${r.ruleCount} ticked of their own` : "";
  return source + scope;
}

/** One project in GET /api/admin/users/{id}/project-access — the resolver's
 *  answer for any account: a Joveo person, an outside person, or an API
 *  integration. `why` is the reason it can (or can't) open the project. */
export interface AccountAccessRow {
  name: string;
  slug: string;
  canOpen: boolean;
  role: "admin" | "editor" | "viewer" | null;
  why: string;
  partners: string[];
  mode: PersonReach["mode"];
  ruleCount: number;
}

const ACCOUNT_WHY: Record<string, string> = {
  super_admin: "Super admin",
  member: "Project member",
  anyone_at_joveo: "Anyone at Joveo",
  not_a_member: "Not a member",
  deactivated: "Account deactivated",
  integration: "API integration",
  private: "Private project",
  partner_locked: "Belongs to one partner",
  not_published: "Not published yet",
};

/** Settings → Users → Access: one line per project. */
export function describeAccountAccess(r: AccountAccessRow): string {
  if (r.why in ACCOUNT_WHY) return ACCOUNT_WHY[r.why];
  // An outside person: the same words as their Doc Access tab.
  return describePersonReach({
    mode: r.mode, ruleCount: r.ruleCount, canRead: r.canOpen, partners: r.partners,
    via: r.canOpen ? r.why as PersonReach["via"] : null,
    reason: r.canOpen ? null : r.why as PersonReach["reason"],
  });
}
